/**
 * Shared utility functions for file handling
 */

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

/**
 * Get file type category from mimetype
 */
export type FileTypeCategory = "pdf" | "word" | "excel" | "image" | "text" | "zip" | "other";

export function getFileTypeCategory(mimetype: string): FileTypeCategory {
  if (mimetype === "application/pdf") return "pdf";
  if (
    mimetype === "application/msword" ||
    mimetype === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return "word";
  if (
    mimetype === "application/vnd.ms-excel" ||
    mimetype === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return "excel";
  if (mimetype.startsWith("image/")) return "image";
  if (mimetype === "text/plain") return "text";
  if (mimetype === "application/zip") return "zip";
  return "other";
}

/**
 * Get file type icon SVG path
 */
export function getFileTypeIcon(mimetype: string): {
  viewBox: string;
  path: string;
  color: string;
} {
  const category = getFileTypeCategory(mimetype);

  switch (category) {
    case "pdf":
      return {
        viewBox: "0 0 24 24",
        path: "M7 18h10V16H7v2zm0-4h10v-2H7v2zm0-4h7V8H7v2zm12-8H5a2 2 0 00-2 2v16a2 2 0 002 2h14a2 2 0 002-2V4a2 2 0 00-2-2zm0 18H5V4h14v16z",
        color: "#DC2626", // red-600
      };
    case "word":
      return {
        viewBox: "0 0 24 24",
        path: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.5l1.5-5 1.5 5 1.5-5 1.5 5h1l-2-7h-1l-1.5 5-1.5-5h-1l-2 7h1z",
        color: "#2563EB", // blue-600
      };
    case "excel":
      return {
        viewBox: "0 0 24 24",
        path: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11zm-4.5-6L11 17l-2.5-3L11 11l2.5 3zm3.5 3l-2.5-3L17 11l-2.5-3L12 11l2.5 3-2.5 3z",
        color: "#059669", // emerald-600
      };
    case "image":
      return {
        viewBox: "0 0 24 24",
        path: "M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z",
        color: "#7C3AED", // violet-600
      };
    case "text":
      return {
        viewBox: "0 0 24 24",
        path: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 10h8v2H8v-2zm0 4h8v2H8v-2z",
        color: "#64748B", // slate-600
      };
    case "zip":
      return {
        viewBox: "0 0 24 24",
        path: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-2 16h-2v-2h2v2zm0-3h-2v-2h2v2zm0-3h-2V9h2v3zm0-4h-2V6h2v2zm2 10h2v-2h-2v2zm0-3h2v-2h-2v2zm0-3h2V9h-2v3zm2-4V4h-2v2h2z",
        color: "#F59E0B", // amber-600
      };
    default:
      return {
        viewBox: "0 0 24 24",
        path: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z",
        color: "#64748B", // slate-600
      };
  }
}
